import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { ApiService } from '../../core/services/api.service';
import { IngestionJob } from '../../core/models/ingestion.models';
import { IngestionListComponent } from './components/ingestion-list/ingestion-list.component';
import { IngestionJobListResponse } from '../../core/models/ingestion.models';
import { FileBrowserComponent } from './components/file-browser/file-browser.component';
import { ParsedFileBrowserComponent } from './components/parsed-file-browser/parsed-file-browser.component';

const POLL_INTERVAL_MS = 4000;

@Component({
  selector: 'app-knowledge-management',
  standalone: true,
  imports: [CommonModule, RouterLink, IngestionListComponent, FileBrowserComponent, ParsedFileBrowserComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './knowledge-management.component.html',
  styleUrls: ['./knowledge-management.component.scss'],
  host: {
    class: 'flex grow flex-col h-full w-full'
  }
})
export class KnowledgeManagementComponent implements OnInit, OnDestroy {
  activeTab: 'files' | 'parsed' | 'jobs' = 'files';
  pendingParsedKey: string | null = null;

  private api = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();
  private pollSub?: Subscription;

  jobs: IngestionJob[] = [];
  isLoading = true;
  listError: string | null = null;

  currentPage = 1;
  readonly pageSize = 20;
  totalCount = 0;

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  toast: string | null = null;
  newJobsCount = 0;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    // Only poll when the jobs tab is active; start tab is 'files'
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.pollSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  setTab(tab: 'files' | 'parsed' | 'jobs'): void {
    this.activeTab = tab;
    if (tab === 'jobs') {
      this.newJobsCount = 0;
      if (!this.pollSub || this.pollSub.closed) {
        this.startPolling();
      }
    } else {
      this.pollSub?.unsubscribe();
    }
    this.cdr.markForCheck();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.pollSub?.unsubscribe();
    this.startPolling();
    this.cdr.markForCheck();
  }

  openParsedFile(parsedS3Key: string): void {
    this.pendingParsedKey = parsedS3Key;
    this.setTab('parsed');
  }

  showToast(message: string, hasNewJob = true): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = message;
    if (hasNewJob) this.newJobsCount++;
    this.cdr.markForCheck();
    this.toastTimer = setTimeout(() => {
      this.toast = null;
      this.cdr.markForCheck();
    }, 4000);

    // Pre-fetch page 1 so latest jobs are ready when the user clicks the tab
    this.api.listIngestionJobs(1, this.pageSize).subscribe({
      next: (resp: IngestionJobListResponse) => {
        this.currentPage = 1;
        this.jobs = resp.results;
        this.totalCount = resp.count ?? 0;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      this.pollSub?.unsubscribe();
    } else if (this.activeTab === 'jobs' && (!this.pollSub || this.pollSub.closed)) {
      this.startPolling();
    }
  };

  private startPolling(): void {
    this.pollSub = timer(0, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.api.listIngestionJobs(this.currentPage, this.pageSize)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (resp: IngestionJobListResponse) => {
          this.jobs = resp.results;
          this.totalCount = resp.count ?? 0;
          this.isLoading = false;
          this.listError = null;
          this.cdr.markForCheck();
        },
        error: err => {
          this.listError = err?.message || 'Failed to load ingestion jobs';
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }
}
